import {
  type FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import axios from 'axios'
import currency from 'currency.js'
import { useStripe } from '@stripe/react-stripe-js'
import { Link, useHistory } from 'react-router-dom'
import { useShallow } from 'zustand/react/shallow'

import Dialog from '../../Dialog'
import useAuth from '../../../hooks/useAuth'
import useCheckoutSummary from '../../../hooks/useCheckoutSummary'
import usePurchaseUploadCredits from '../../../hooks/usePurchaseUploadCredits'
import useRestoreAnalysedFile from '../../../hooks/useRestoreAnalysedFile'
import useSubmitCheckoutUpload from '../../../hooks/useSubmitCheckoutUpload'
import useNewUpload from '../../../hooks/useNewUpload'
import CheckoutAddons from './CheckoutAddons'
import CheckoutCreditSummary from './CheckoutCreditSummary'
import CheckoutPaymentSummary, {
  type TPaymentItem,
} from './CheckoutPaymentSummary'
import { PrivateAPI } from '../../../utils/api'
import NewUploadWarrantyModal from './WarrantyModal'

type Props = {
  formId: string
}

type TDtcOnlyPaymentIntentResponse = {
  clientSecret: string
  paymentIntentId: string
}

type TDtcOnlyPaymentStatusResponse = {
  status: 'FAILED' | 'PENDING' | 'SUCCEEDED'
}

type TUploadCreditPaymentStatusResponse = {
  status: 'FAILED' | 'PENDING' | 'SUCCEEDED'
}

type TUploadCreationStatusResponse =
  | {
      status: 'PENDING'
    }
  | {
      error: string
      manualBuildUrl?: string
      status: 'FAILED'
    }
  | {
      addonPaymentClientSecret?: string
      status: 'SUCCEEDED'
      upload: Record<string, unknown> & {
        id: string
      }
    }

const isTransientPollError = (err: unknown) => {
  if (!axios.isAxiosError(err)) {
    return false
  }

  const status = err.response?.status

  return !status || status >= 500
}

const Checkout = (props: Props) => {
  const { formId } = props
  const history = useHistory()
  const stripe = useStripe()
  const [auth, setAuth] = useAuth()
  const { checkout, setNewUploadState } = useNewUpload(
    useShallow((state) => ({
      checkout: state.checkout,
      setNewUploadState: state.setState,
    })),
  )
  const purchaseUploadCredits = usePurchaseUploadCredits()
  const submitCheckoutUpload = useSubmitCheckoutUpload()
  const [paymentMethodModalOpen, setPaymentMethodModalOpen] = useState(false)
  const [warrantyModalOpen, setWarrantyModalOpen] = useState(false)
  const { analysedFile, isLoadingAnalysedFile } = useRestoreAnalysedFile()

  const { data: checkoutSummary, isLoading: isLoadingCheckoutSummary } =
    useCheckoutSummary(analysedFile?.id || '', Boolean(analysedFile?.id))

  const availableAddons = checkoutSummary?.availableAddons || []
  const creditsToPurchase = checkout.creditsToPurchase
  const manualBuildUrl = checkout.manualBuildUrl
  const submitError = checkout.submitError

  const setCheckout = useCallback(
    (updates: Partial<typeof checkout>) => {
      setNewUploadState({
        checkout: {
          ...checkout,
          ...updates,
        },
      })
    },
    [checkout, setNewUploadState],
  )

  const hasWarranty12 = availableAddons.some(
    (addon) => addon.type === 'WARRANTY_12',
  )
  const hasWarranty24 = availableAddons.some(
    (addon) => addon.type === 'WARRANTY_24',
  )
  const hasWarrantyAddon =
    analysedFile?.input.vehicleType === 'CAR' &&
    (parseInt(analysedFile.input.mileage, 10) || 0) < 100000 &&
    (hasWarranty12 || hasWarranty24)

  const hasIxiSurcharge = availableAddons.some(
    (addon) => addon.type === 'IXI_FLASHER_SURCHARGE',
  )
  const isDtcOnlyCheckoutFlow = Boolean(
    checkoutSummary?.dtcOnlyPayment.required,
  )
  const isDtcOnlyCardCheckoutFlow =
    checkoutSummary?.dtcOnlyPayment.mode === 'CARD'
  const isManualCheckoutFlow = Boolean(
    analysedFile &&
    (analysedFile.manual ||
      ((analysedFile.auto?.availableDTCs.length || 0) === 0 &&
        (analysedFile.auto?.availableMods.length || 0) === 0)),
  )

  const creditItems = useMemo(() => {
    if (!checkoutSummary) {
      return []
    }

    const localCreditItems = [...checkoutSummary.creditItems]

    if (
      checkoutSummary.dtcOnlyPayment.mode === 'CARD' &&
      localCreditItems.length < 1
    ) {
      localCreditItems.push({
        amount: 0,
        description: 'DTC only upload - no credit charge',
      })
    }

    if (isManualCheckoutFlow && checkout.priority) {
      localCreditItems.push({
        amount: 0.5,
        description: 'Priority file add-on',
      })
    }

    if (!isDtcOnlyCheckoutFlow && checkout.ecuWarranty) {
      localCreditItems.push({
        amount: 0.25,
        description: 'ECU warranty add-on',
      })
    }

    return localCreditItems
  }, [
    checkoutSummary,
    checkout.ecuWarranty,
    checkout.priority,
    isDtcOnlyCheckoutFlow,
    isManualCheckoutFlow,
  ])

  const creditsTotal = useMemo(() => {
    return creditItems.reduce((total, item) => total + item.amount, 0)
  }, [creditItems])

  const creditDifference = useMemo(() => {
    if (!checkoutSummary) {
      return 0
    }

    return creditsTotal - checkoutSummary.creditBalance
  }, [checkoutSummary, creditsTotal])

  const waitForDtcOnlyPaymentStatus = useCallback(
    async (analysedFileId: string) => {
      let dtcOnlyPaymentStatus: TDtcOnlyPaymentStatusResponse['status'] =
        'PENDING'

      for (let attempt = 0; attempt < 60; attempt += 1) {
        try {
          const paymentStatusResponse =
            await PrivateAPI<TDtcOnlyPaymentStatusResponse>({
              url: `/uploads/analyse/${analysedFileId}/dtc-only-payment-status`,
              method: 'get',
            })

          dtcOnlyPaymentStatus = paymentStatusResponse.data.status
        } catch (err) {
          if (!isTransientPollError(err)) {
            throw err
          }
        }

        if (dtcOnlyPaymentStatus !== 'PENDING') {
          break
        }

        await new Promise((resolve) => {
          window.setTimeout(resolve, 1000)
        })
      }

      return dtcOnlyPaymentStatus
    },
    [],
  )

  const waitForUploadCreditPaymentStatus = useCallback(
    async (orderId: string) => {
      let uploadCreditPaymentStatus: TUploadCreditPaymentStatusResponse['status'] =
        'PENDING'

      for (let attempt = 0; attempt < 60; attempt += 1) {
        try {
          const paymentStatusResponse =
            await PrivateAPI<TUploadCreditPaymentStatusResponse>({
              url: `/billing/upload/checkout/${orderId}/status`,
              method: 'get',
            })

          uploadCreditPaymentStatus = paymentStatusResponse.data.status
        } catch (err) {
          if (!isTransientPollError(err)) {
            throw err
          }
        }

        if (uploadCreditPaymentStatus !== 'PENDING') {
          break
        }

        await new Promise((resolve) => {
          window.setTimeout(resolve, 1000)
        })
      }

      return uploadCreditPaymentStatus
    },
    [],
  )

  const waitForUploadCreationStatus = useCallback(
    async (analysedFileId: string) => {
      while (true) {
        try {
          const uploadCreationResponse =
            await PrivateAPI<TUploadCreationStatusResponse>({
              url: `/uploads/analyse/${analysedFileId}/upload-status`,
              method: 'get',
            })

          if (uploadCreationResponse.data.status !== 'PENDING') {
            return uploadCreationResponse.data
          }
        } catch (err) {
          if (!isTransientPollError(err)) {
            throw err
          }
        }

        await new Promise((resolve) => {
          window.setTimeout(resolve, 2000)
        })
      }
    },
    [],
  )

  const setAnalysedFileDtcOnlyPaymentStatus = useCallback(
    (
      analysedFileState: NonNullable<typeof analysedFile>,
      paymentIntentId: string,
      status: TDtcOnlyPaymentStatusResponse['status'],
    ) => {
      setNewUploadState({
        analysedFile: {
          ...analysedFileState,
          dtcOnlyPayment: {
            amount: checkoutSummary?.dtcOnlyPayment.amount || 0,
            paymentIntentId,
            required: true,
            status,
            totalAmount: checkoutSummary?.dtcOnlyPayment.totalAmount || 0,
            vatAmount: checkoutSummary?.dtcOnlyPayment.vatAmount || 0,
          },
        },
      })
    },
    [checkoutSummary, setNewUploadState],
  )

  const ensureDtcOnlyPaymentConfirmed = useCallback(
    async (
      analysedFileState: NonNullable<typeof analysedFile>,
      paymentMethodId: string,
    ) => {
      let dtcOnlyPaymentStatus = checkoutSummary?.dtcOnlyPayment.status

      if (
        analysedFileState.dtcOnlyPayment?.paymentIntentId &&
        dtcOnlyPaymentStatus !== 'SUCCEEDED'
      ) {
        dtcOnlyPaymentStatus = await waitForDtcOnlyPaymentStatus(
          analysedFileState.id,
        )

        setAnalysedFileDtcOnlyPaymentStatus(
          analysedFileState,
          analysedFileState.dtcOnlyPayment.paymentIntentId,
          dtcOnlyPaymentStatus,
        )
      }

      if (dtcOnlyPaymentStatus === 'SUCCEEDED') {
        return
      }

      const paymentIntentResponse =
        await PrivateAPI<TDtcOnlyPaymentIntentResponse>({
          url: `/uploads/analyse/${analysedFileState.id}/dtc-only-payment-intent`,
          method: 'post',
        })

      setAnalysedFileDtcOnlyPaymentStatus(
        analysedFileState,
        paymentIntentResponse.data.paymentIntentId,
        'PENDING',
      )

      const paymentResult = await stripe?.confirmCardPayment(
        paymentIntentResponse.data.clientSecret,
        {
          payment_method: paymentMethodId,
        },
      )

      if (paymentResult?.error) {
        throw new Error(paymentResult.error.message)
      }

      dtcOnlyPaymentStatus = await waitForDtcOnlyPaymentStatus(
        analysedFileState.id,
      )

      setAnalysedFileDtcOnlyPaymentStatus(
        analysedFileState,
        paymentIntentResponse.data.paymentIntentId,
        dtcOnlyPaymentStatus,
      )

      if (dtcOnlyPaymentStatus === 'FAILED') {
        throw new Error(
          'The DTC-only payment failed. Please try again with a different payment method.',
        )
      }

      if (dtcOnlyPaymentStatus !== 'SUCCEEDED') {
        throw new Error(
          'The DTC-only payment is still being confirmed. Please wait a moment and try again.',
        )
      }
    },
    [
      checkoutSummary?.dtcOnlyPayment.status,
      setAnalysedFileDtcOnlyPaymentStatus,
      stripe,
      waitForDtcOnlyPaymentStatus,
    ],
  )

  const submitUpload = useCallback(
    async (skipWarrantyPrompt: boolean = false) => {
      setCheckout({ isCreating: false, manualBuildUrl: '', submitError: '' })

      if (!analysedFile || !checkoutSummary || !stripe) {
        return
      }

      if (
        !skipWarrantyPrompt &&
        !isDtcOnlyCheckoutFlow &&
        hasWarrantyAddon &&
        !checkout.warranty.required
      ) {
        setWarrantyModalOpen(true)
        return
      }

      if (creditDifference > 0) {
        if (!checkoutSummary.isMaster) {
          setCheckout({ submitError: 'Insufficient credit balance' })
          return
        }

        if (!creditsToPurchase) {
          setCheckout({
            submitError: `Add ${creditDifference} credit${
              creditDifference === 1 ? '' : 's'
            } to checkout before continuing.`,
          })
          return
        }
      }

      setCheckout({ isCreating: true })

      try {
        let orderId: string | undefined

        if (creditsToPurchase) {
          const creditPurchase = await purchaseUploadCredits.mutateAsync({
            numberOfCredits: creditsToPurchase,
          })

          const paymentMethodId =
            checkoutSummary.paymentMethods[
              checkout.chosenPaymentMethodIndex
            ]?.id

          const creditResult = await stripe.confirmCardPayment(
            creditPurchase.clientSecret,
            {
              payment_method: paymentMethodId,
            },
          )

          if (creditResult.error) {
            setCheckout({
              isCreating: false,
              submitError: creditResult.error.message,
            })
            return
          }

          if (creditResult.paymentIntent.status !== 'succeeded') {
            setCheckout({
              isCreating: false,
              submitError: 'An unexpected error occured',
            })
            return
          }

          const uploadCreditPaymentStatus =
            await waitForUploadCreditPaymentStatus(creditPurchase.orderId)

          if (uploadCreditPaymentStatus === 'FAILED') {
            setCheckout({
              isCreating: false,
              submitError:
                'The upload credit payment failed. Please try again with a different payment method.',
            })
            return
          }

          if (uploadCreditPaymentStatus !== 'SUCCEEDED') {
            setCheckout({
              isCreating: false,
              submitError:
                'The upload credit payment is still being confirmed. Please wait a moment and try again.',
            })
            return
          }

          orderId = creditPurchase.orderId
        }

        if (isDtcOnlyCardCheckoutFlow) {
          // DTC-only uploads must complete their dedicated card-payment flow
          // before the upload itself can be created.
          const paymentMethodId =
            checkoutSummary.paymentMethods[
              checkout.chosenPaymentMethodIndex
            ]?.id

          if (!paymentMethodId) {
            setCheckout({
              isCreating: false,
              submitError: 'You must have a payment method',
            })
            return
          }

          await ensureDtcOnlyPaymentConfirmed(analysedFile, paymentMethodId)
        }

        let response: TUploadCreationStatusResponse =
          await submitCheckoutUpload.mutateAsync({
            analysedFileId: analysedFile.id,
            chosenPaymentMethodIndex:
              checkout.chosenPaymentMethodIndex,
            checkoutSummary,
            ecuWarranty: isDtcOnlyCheckoutFlow
              ? false
              : checkout.ecuWarranty,
            orderId,
            priority: isDtcOnlyCheckoutFlow
              ? false
              : checkout.priority,
            // prettier-ignore
            warranty: isDtcOnlyCheckoutFlow
            ? {
              ...checkout.warranty,
              required: false,
            }
            : checkout.warranty,
          })

        if (response.status === 'PENDING') {
          response = await waitForUploadCreationStatus(analysedFile.id)
        }

        if (response.status === 'FAILED') {
          setCheckout({
            isCreating: false,
            manualBuildUrl: response.manualBuildUrl || '',
            submitError: response.error,
          })
          return
        }

        if (auth.membership.dealer.credit) {
          setAuth({
            ...auth,
            membership: {
              ...auth.membership,
              dealer: {
                ...auth.membership.dealer,
                credit: {
                  ...auth.membership.dealer.credit,
                  balance:
                    auth.membership.dealer.credit.balance -
                    creditsTotal +
                    (creditsToPurchase || 0),
                },
              },
            },
          })
        }

        if (response.addonPaymentClientSecret) {
          const paymentMethodId =
            checkoutSummary.paymentMethods[
              checkout.chosenPaymentMethodIndex
            ]?.id

          try {
            const result = await stripe.confirmCardPayment(
              response.addonPaymentClientSecret,
              {
                payment_method: paymentMethodId,
              },
            )

            if (result.error) {
              history.push(`/uploads/view/${response.upload.id}`, {
                upload: response.upload,
              })
              return
            }
          } catch {
            history.push(`/uploads/view/${response.upload.id}`, {
              upload: response.upload,
            })
            return
          }
        }

        history.push(`/uploads/view/${response.upload.id}`, {
          upload: response.upload,
        })
      } catch (err) {
        setCheckout({
          isCreating: false,
          manualBuildUrl: '',
          submitError:
            err instanceof Error ? err.message : 'An unexpected error occured',
        })
      }
    },
    [
      analysedFile,
      creditDifference,
      creditsToPurchase,
      checkoutSummary,
      history,
      auth,
      checkout.chosenPaymentMethodIndex,
      checkout.ecuWarranty,
      checkout.priority,
      checkout.warranty,
      purchaseUploadCredits,
      setAuth,
      setCheckout,
      hasWarrantyAddon,
      isDtcOnlyCardCheckoutFlow,
      ensureDtcOnlyPaymentConfirmed,
      setWarrantyModalOpen,
      stripe,
      submitCheckoutUpload,
      waitForUploadCreditPaymentStatus,
      waitForUploadCreationStatus,
      isDtcOnlyCheckoutFlow,
      creditsTotal,
    ],
  )

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      await submitUpload()
    },
    [submitUpload],
  )

  const cardItems = useMemo(() => {
    if (!checkoutSummary) {
      return []
    }

    const items: TPaymentItem[] = []

    if (checkoutSummary.dtcOnlyPayment.mode === 'CARD') {
      items.push({
        amount: checkoutSummary.dtcOnlyPayment.amount,
        description: 'DTC only upload',
        vat: checkoutSummary.dtcOnlyPayment.vatAmount,
      })
    }

    const warrantyAddon = checkoutSummary.availableAddons.find(
      (addon) => addon.type === checkout.warranty.type,
    )

    if (checkout.warranty.required && warrantyAddon) {
      items.push({
        amount: warrantyAddon.cost,
        vat: checkoutSummary.shouldChargeVat ? warrantyAddon.cost * 0.2 : 0,
        description:
          checkout.warranty.type === 'WARRANTY_12'
            ? '12 month mechanical warranty'
            : '24 month mechanical warranty',
      })
    }

    const ixiAddon = checkoutSummary.availableAddons.find(
      (addon) => addon.type === 'IXI_FLASHER_SURCHARGE',
    )

    if (
      !checkoutSummary.dtcOnlyPayment.required &&
      analysedFile?.input.tune.tuningTool === 'IXI_FLASHER' &&
      ixiAddon
    ) {
      items.push({
        amount: ixiAddon.cost,
        vat: checkoutSummary.shouldChargeVat ? ixiAddon.cost * 0.2 : 0,
        description: 'IXI Flasher tool surcharge',
      })
    }

    if (creditsToPurchase) {
      const amount = currency(
        checkoutSummary.adHocCreditBaseCost * creditsToPurchase,
      ).value

      items.push({
        amount,
        vat: currency(checkoutSummary.shouldChargeVat ? amount * 0.2 : 0).value,
        description: `Purchase of ${creditsToPurchase} credit${
          creditsToPurchase === 1 ? '' : 's'
        }`,
      })
    }

    return items
  }, [
    analysedFile?.input.tune.tuningTool,
    creditsToPurchase,
    checkoutSummary,
    checkout.warranty,
  ])

  const [cardTotal, cardVatTotal] = useMemo(() => {
    return cardItems.reduce(
      (totals, item) => {
        return [
          totals[0] + item.amount + (item.vat || 0),
          totals[1] + (item.vat || 0),
        ]
      },
      [0, 0],
    )
  }, [cardItems])

  const chosenPaymentMethod =
    checkoutSummary?.paymentMethods[checkout.chosenPaymentMethodIndex]
  const shouldShowPurchaseAdHocCredits = Boolean(
    checkoutSummary?.isMaster && creditDifference > 0 && !creditsToPurchase,
  )
  const shouldDisableCreateUpload = Boolean(
    checkoutSummary &&
    (checkoutSummary.requiresPaymentMethod ||
      (creditDifference > 0 && !checkoutSummary.isMaster)),
  )

  useEffect(() => {
    if (!creditsToPurchase) {
      return
    }

    if (creditDifference > 0 && creditsToPurchase !== creditDifference) {
      setCheckout({ creditsToPurchase: creditDifference })
      return
    }

    if (creditDifference <= 0) {
      setCheckout({ creditsToPurchase: null })
    }
  }, [creditDifference, creditsToPurchase, setCheckout])

  useEffect(() => {
    if (checkout.createUploadDisabled === shouldDisableCreateUpload) {
      return
    }

    setCheckout({
      createUploadDisabled: shouldDisableCreateUpload,
    })
  }, [
    checkout.createUploadDisabled,
    setCheckout,
    shouldDisableCreateUpload,
  ])

  useEffect(() => {
    if (
      !isDtcOnlyCheckoutFlow ||
      (!checkout.ecuWarranty &&
        !checkout.priority &&
        !checkout.warranty.required)
    ) {
      return
    }

    setCheckout({
      ecuWarranty: false,
      priority: false,
        warranty: {
        ...checkout.warranty,
        required: false,
      },
    })
  }, [
    isDtcOnlyCheckoutFlow,
    checkout.ecuWarranty,
    checkout.priority,
    checkout.warranty,
    setCheckout,
  ])

  const getPaymentMethodText = () => {
    if (!chosenPaymentMethod) {
      return ''
    }

    const { card, nickname } = chosenPaymentMethod
    const { brand, last4 } = card

    if (nickname) {
      return `${brand.toUpperCase()} ending in ${last4} (${nickname})`
    }

    return `${brand.toUpperCase()} ending in ${last4}`
  }

  if (!analysedFile || isLoadingAnalysedFile || isLoadingCheckoutSummary) {
    return (
      <div className="flex flex-1 items-center justify-center p-12">
        <span className="spinner" />
      </div>
    )
  }

  if (!checkoutSummary) {
    return (
      <p className="p-6 text-sm text-red-600">
        Failed to load checkout details.
      </p>
    )
  }

  return (
    <>
      <form id={formId} onSubmit={handleSubmit}>
        <div className="grid grid-cols-5 sm:grid-cols-6 md:grid-cols-5">
          <CheckoutAddons
            analysedFileTuningTool={analysedFile.input.tune.tuningTool}
            checkout={checkout}
            hasIxiSurcharge={hasIxiSurcharge}
            hasWarranty12={hasWarranty12}
            hasWarranty24={hasWarranty24}
            hasWarrantyAddon={hasWarrantyAddon}
            isDisabled={checkout.isCreating}
            isDtcOnlyCheckoutFlow={isDtcOnlyCheckoutFlow}
            isManualCheckoutFlow={isManualCheckoutFlow}
            setCheckout={(nextCheckout) =>
              setNewUploadState({ checkout: nextCheckout })
            }
          />

          <div className="col-span-5 p-6 sm:col-span-3">
            <div>
              <h3 className="block text-sm font-medium leading-5 text-black">
                Checkout summary
              </h3>
              <p className="pb-4 text-sm text-gray-600">
                Please review the checkout summary to ensure you're happy with
                the cost of this upload.
              </p>
            </div>

            <CheckoutCreditSummary
              creditBalance={checkoutSummary.creditBalance}
              creditDifference={creditDifference}
              creditItems={creditItems}
              creditsToPurchase={creditsToPurchase}
              creditsTotal={creditsTotal}
              isDisabled={checkout.isCreating}
              isMaster={checkoutSummary.isMaster}
              setCreditsToPurchase={(nextCreditsToPurchase) =>
                setCheckout({ creditsToPurchase: nextCreditsToPurchase })
              }
              shouldShowPurchaseAdHocCredits={shouldShowPurchaseAdHocCredits}
            />

            <CheckoutPaymentSummary
              cardItems={cardItems}
              cardTotal={cardTotal}
              cardVatTotal={cardVatTotal}
              isDisabled={checkout.isCreating}
              onOpenPaymentMethodModal={() => setPaymentMethodModalOpen(true)}
              paymentMethodsLength={checkoutSummary.paymentMethods.length}
              paymentMethodText={getPaymentMethodText()}
            />

            {(submitError ||
              purchaseUploadCredits.error?.message ||
              submitCheckoutUpload.error?.message) && (
              <div className="my-4 space-y-3">
                <p className="text-sm text-red-600">
                  {submitError ||
                    purchaseUploadCredits.error?.message ||
                    submitCheckoutUpload.error?.message}
                </p>
                {manualBuildUrl ? (
                  <Link
                    className="inline-flex text-sm font-medium text-gray-900 underline"
                    to={manualBuildUrl}
                  >
                    Create a manual upload instead
                  </Link>
                ) : null}
              </div>
            )}

            <p className="text-xs text-gray-600 my-4">
              By clicking “Create upload" you are agreeing to our terms and
              conditions. The amount of credits listed above will be deducted
              from your account immediately. If you have selected any add-ons we
              will also collect payment automatically using your chosen payment
              method.
            </p>
          </div>
        </div>
      </form>

      <div />
      <Dialog
        isOpen={paymentMethodModalOpen}
        title="Choose payment method"
        buttonProps={null}
      >
        <div className="space-y-3 mt-4">
          {checkoutSummary.paymentMethods.map((method, index) => (
            <button
              className="flex w-full items-center justify-between rounded-md border border-gray-200 px-4 py-3 text-left hover:bg-gray-50"
              type="button"
              key={method.id}
              onClick={() => {
                setNewUploadState({
                  checkout: {
                    ...checkout,
                    chosenPaymentMethodIndex: index,
                  },
                })
                setPaymentMethodModalOpen(false)
              }}
            >
              <span className="text-sm text-black">
                {method.nickname
                  ? `${method.card.brand.toUpperCase()} ending in ${method.card.last4} (${method.nickname})`
                  : `${method.card.brand.toUpperCase()} ending in ${method.card.last4}`}
              </span>
              {checkout.chosenPaymentMethodIndex === index && (
                <span className="text-xs font-medium text-gray-600">
                  Selected
                </span>
              )}
            </button>
          ))}
        </div>
      </Dialog>
      <NewUploadWarrantyModal
        isOpen={warrantyModalOpen}
        setIsOpen={() => setWarrantyModalOpen(false)}
        onContinue={() => {
          setWarrantyModalOpen(false)
          void submitUpload(true)
        }}
      />
    </>
  )
}

export default Checkout
